import type { SyncOp } from '@ims/core'
import {
  LOCAL_SCHEMA_VERSION,
  encodeRow,
  isTableName,
  localSchemaSql,
  syncedTables,
  upsertSql,
  type PullCursor,
  type TableName,
} from '@ims/data'
import type { SqlDriver, SqlParam, SqlStatement } from './driver.ts'

// Bookkeeping tables (prefixed with _) live next to the synced tables in the
// same per-tenant database file.
const META_SQL = [
  `CREATE TABLE IF NOT EXISTS _meta (key TEXT PRIMARY KEY, value TEXT NOT NULL) STRICT;`,
  `CREATE TABLE IF NOT EXISTS _outbox (
    seq        INTEGER PRIMARY KEY AUTOINCREMENT,
    tx_id      TEXT NOT NULL UNIQUE,
    ops        TEXT NOT NULL,
    tables     TEXT NOT NULL,
    created_at TEXT NOT NULL,
    status     TEXT NOT NULL DEFAULT 'pending' CHECK (status IN ('pending', 'failed')),
    attempts   INTEGER NOT NULL DEFAULT 0,
    last_code  TEXT,
    last_error TEXT,
    failed_at  TEXT
  ) STRICT;`,
  // Rows touched by unsynced txs: pulls must not overwrite them.
  `CREATE TABLE IF NOT EXISTS _pending_rows (
    tx_id  TEXT NOT NULL,
    tbl    TEXT NOT NULL,
    row_id TEXT NOT NULL,
    PRIMARY KEY (tx_id, tbl, row_id)
  ) STRICT;`,
  `CREATE INDEX IF NOT EXISTS _pending_rows_row ON _pending_rows (tbl, row_id);`,
]

export type MetaKey =
  | 'schema_version'
  | 'tenant_id'
  | 'pull_cursor'
  | 'last_pulled_at'
  | 'entitlements'
  | 'last_checkin'

export async function getMeta(driver: SqlDriver, key: MetaKey): Promise<string | null> {
  const rows = await driver.all<{ value: string }>('SELECT value FROM _meta WHERE key = ?', [key])
  return rows[0]?.value ?? null
}

export function setMetaSql(key: MetaKey, value: string | null): SqlStatement {
  return value === null
    ? { sql: 'DELETE FROM _meta WHERE key = ?', params: [key] }
    : {
        sql: 'INSERT INTO _meta (key, value) VALUES (?, ?) ON CONFLICT (key) DO UPDATE SET value = excluded.value',
        params: [key, value],
      }
}

export async function getCursor(driver: SqlDriver): Promise<PullCursor | null> {
  const raw = await getMeta(driver, 'pull_cursor')
  return raw ? (JSON.parse(raw) as PullCursor) : null
}

/** Statements that apply one op locally (same semantics as the server's merge). */
export function opStatements(op: SyncOp, now: string): SqlStatement[] {
  if (!isTableName(op.table)) throw new Error(`unknown table ${op.table}`)
  const table: TableName = op.table
  if (op.op === 'delete') {
    return [
      {
        sql: `UPDATE ${table} SET deleted_at = ?, updated_at = ? WHERE id = ? AND deleted_at IS NULL`,
        params: [now, now, String(op.row.id)],
      },
    ]
  }
  const { sql, columns } = upsertSql(table)
  const values = encodeRow(table, op.row)
  return [{ sql, params: columns.map((c) => values[c] ?? null) }]
}

export interface OpenResult {
  /** True when the synced tables were (re)created and need a full pull. */
  rebuilt: boolean
}

/**
 * Creates or upgrades the local schema. On a LOCAL_SCHEMA_VERSION change the
 * synced tables are dropped and recreated (they are a cache of the server),
 * the pull cursor is reset, and pending outbox ops are re-applied so unsynced
 * work stays visible. The outbox itself is never dropped.
 */
export async function openLocalDb(
  driver: SqlDriver,
  tenantId: string,
  now: string,
): Promise<OpenResult> {
  await driver.batch(META_SQL.map((sql) => ({ sql })))

  const storedTenant = await getMeta(driver, 'tenant_id')
  if (storedTenant && storedTenant !== tenantId) {
    throw new Error(`local database belongs to tenant ${storedTenant}, not ${tenantId}`)
  }

  const version = await getMeta(driver, 'schema_version')
  if (version === String(LOCAL_SCHEMA_VERSION)) return { rebuilt: false }

  const pending = await driver.all<{ ops: string }>(`SELECT ops FROM _outbox ORDER BY seq`)
  const statements: SqlStatement[] = []
  if (version !== null) {
    statements.push({ sql: 'DROP VIEW IF EXISTS current_stock' })
    statements.push({ sql: 'DROP VIEW IF EXISTS batch_stock' })
    for (const table of [...syncedTables].reverse())
      statements.push({ sql: `DROP TABLE IF EXISTS ${table.name}` })
  }
  for (const sql of localSchemaSql()) statements.push({ sql })
  for (const row of pending) {
    for (const op of JSON.parse(row.ops) as SyncOp[]) statements.push(...opStatements(op, now))
  }
  statements.push(setMetaSql('schema_version', String(LOCAL_SCHEMA_VERSION)))
  statements.push(setMetaSql('tenant_id', tenantId))
  statements.push(setMetaSql('pull_cursor', null))
  statements.push(setMetaSql('last_pulled_at', null))
  await driver.batch(statements)
  return { rebuilt: true }
}

export function placeholders(values: readonly SqlParam[]): string {
  return values.map(() => '?').join(', ')
}
